import { createContext, ReactNode } from "react";
import { useRouter } from "next/router";
import { invert } from "lodash";
import {
  ANALYTICS_INDEX,
  BRAND_PROMOTION_EVENTS,
  BRAND_PROMOTIONS_INDEX,
  EMAIL_CAMPAIGNS_INDEX,
  FORMS_INDEX,
  PURCHASE_ORDERS_INDEX,
  REVIEW_PROGRAMS_INDEX,
  SALES_PLAN_INDEX,
} from "lib/routing/links";
import { Module } from "__generated__/gql/graphql";
import { useBrandModules } from "./access";

const RESTRICTED_PATHS_BY_MODULE: Record<Module, string> = {
  [Module.ANALYTICS]: ANALYTICS_INDEX,
  [Module.EMAIL_CAMPAIGNS]: EMAIL_CAMPAIGNS_INDEX,
  [Module.FORMS]: FORMS_INDEX,
  // Match the current interface before the broader legacy Promotions path.
  [Module.PROMOTIONS]: BRAND_PROMOTION_EVENTS,
  [Module.PROMOTIONS_LEGACY]: BRAND_PROMOTIONS_INDEX,
  [Module.PURCHASE_ORDERS]: PURCHASE_ORDERS_INDEX,
  [Module.REVIEW_PROGRAMS]: REVIEW_PROGRAMS_INDEX,
  [Module.SALES_PLANNER]: SALES_PLAN_INDEX,
};

const MODULES_BY_RESTRICTED_PATHS = invert(
  RESTRICTED_PATHS_BY_MODULE,
) as Record<string, Module>;

const RESTRICTED_PATHS = Object.keys(MODULES_BY_RESTRICTED_PATHS);

export function useRestrictedModulePath() {
  const { pathname } = useRouter();
  const modules = useBrandModules();

  const restrictedPath = RESTRICTED_PATHS.find((p) => pathname.startsWith(p));

  if (!restrictedPath) return false;

  if (!modules) return;

  const restrictedModule = MODULES_BY_RESTRICTED_PATHS[restrictedPath];

  if (modules.includes(restrictedModule)) return false;

  return restrictedModule;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const RestrictedModulePathContext =
  createContext<ReturnType<typeof useRestrictedModulePath>>(false);

type RestrictedModulePathProviderProps = {
  children: ReactNode;
};

export function RestrictedModulePathProvider({
  children,
}: RestrictedModulePathProviderProps) {
  const restrictedModule = useRestrictedModulePath();
  return (
    <RestrictedModulePathContext.Provider value={restrictedModule}>
      {children}
    </RestrictedModulePathContext.Provider>
  );
}
